import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUsers, deleteUsers } from "../../api/Calling";
import { toast } from "react-toastify";
import formatDateTime from "../../components/formatDateTime";
import {
  FaUsers,
  FaTrash,
  FaExclamationTriangle,
  FaBan,
  FaCheckCircle,
} from "react-icons/fa";
import { MdClose } from "react-icons/md";

const calculateDateRange = (filters) => {
  const today = new Date();
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  let startDate = "";
  let endDate = formatDate(today);

  switch (filters.dateRangeType) {
    case "today":
      startDate = formatDate(today);
      break;
    case "yesterday":
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      startDate = formatDate(yesterday);
      endDate = formatDate(yesterday);
      break;
    case "last15":
      const last15 = new Date(today);
      last15.setDate(last15.getDate() - 15);
      startDate = formatDate(last15);
      break;
    case "last30":
      const last30 = new Date(today);
      last30.setDate(last30.getDate() - 30);
      startDate = formatDate(last30);
      break;
    case "last6months":
      const last6months = new Date(today);
      last6months.setMonth(last6months.getMonth() - 6);
      startDate = formatDate(last6months);
      break;
    case "custom":
      startDate = filters.customStartDate;
      endDate = filters.customEndDate;
      break;
    default:
      startDate = formatDate(today);
  }

  return { startDate, endDate };
};

const Users = () => {
  const navigate = useNavigate();

  const [usersData, setUsersData] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSearch, setUsersSearch] = useState("");
  const [usersPagination, setUsersPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });
  const [usersSelectedIds, setUsersSelectedIds] = useState([]);

  const [filterBlock, setFilterBlock] = useState("all");
  const [filterUPid, setFilterUPid] = useState("all");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterType] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateRangeType, setDateRangeType] = useState("today");

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      let params = `?page=${usersPagination.page}&limit=${usersPagination.limit}`;

      if (usersSearch) params += `&search=${usersSearch}`;
      if (filterBlock !== "all") params += `&block=${filterBlock === "blocked"}`;
      if (filterUPid !== "all") params += `&u_pid=${filterUPid}`;

      const { startDate, endDate } = calculateDateRange({
        dateRangeType,
        customStartDate: filterStartDate,
        customEndDate: filterEndDate,
      });

      if (startDate) params += `&startDate=${startDate}`;
      if (endDate) params += `&endDate=${endDate}`;
      if (filterType) params += `&filterType=${filterType}`;
      if (sortOrder) params += `&sort=${sortOrder}`;

      const resp = await getUsers(params);

      if (resp?.status && Array.isArray(resp.data)) {
        setUsersData(resp.data);
        setUsersPagination({
          page: resp.currentPage,
          limit: resp.limit,
          total: resp.total,
          totalPages: resp.totalPages,
        });
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load users");
    }
    setUsersLoading(false);
  };

  useEffect(() => {
    fetchUsers();
  }, [usersPagination.page]);

  const handleApplyFilters = () => {
    if (usersPagination.page === 1) {
      fetchUsers();
    } else {
      setUsersPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const openDeleteConfirmation = (ids) => {
    setDeleteTarget(ids);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const resp = await deleteUsers({ ids: deleteTarget });
      if (resp?.status) {
        toast.success(resp.message || "Deleted successfully");
        setUsersSelectedIds([]);
        fetchUsers();
      } else {
        toast.error(resp?.message || "Delete failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
    } finally {
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setUsersSelectedIds(usersData.map((item) => item._id));
    } else {
      setUsersSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (usersSelectedIds.includes(id)) {
      setUsersSelectedIds(usersSelectedIds.filter((item) => item !== id));
    } else {
      setUsersSelectedIds([...usersSelectedIds, id]);
    }
  };

  const totalPurchases = usersData.reduce((sum, user) => sum + (user.u_purchased || 0), 0);
  const activeUsers = usersData.filter((user) => user.u_status).length;
  const blockedUsers = usersData.filter((user) => !user.u_status).length;

  return (
    <>
      {/* HEADER */}
      <div className="mb-10">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 mb-8">
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-main-blue rounded-xl shadow-lg">
                <FaUsers className="text-2xl text-main-white" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-main-font tracking-tight">
                User Management
              </h1>
            </div>
            <p className="text-main-subfont text-base lg:text-lg font-medium ml-16">
              Manage all registered users
            </p>
          </div>
        </div>

        {/* STATS CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-8">
          <div className="stat-card text-main-white p-6 rounded-2xl shadow-lg relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-main-white/10 rounded-full -mr-8 -mt-8"></div>
            <p className="text-sm font-medium opacity-90 mb-1 relative z-10">Total Users</p>
            <p className="text-4xl font-bold relative z-10">{usersPagination.total}</p>
          </div>
        </div>

        {/* FILTERS & SEARCH */}
        <div className="bg-main-white rounded-2xl shadow-lg border border-main-border p-6 mb-6">
          <div className="flex flex-col gap-4">
            <div className="w-full">
              <input
                type="text"
                placeholder="Search name, email, device, PID..."
                value={usersSearch}
                onChange={(e) => setUsersSearch(e.target.value)}
                className="mono w-full px-4 py-3 border-2 border-main-border rounded-xl text-base
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <select
                value={dateRangeType}
                onChange={(e) => setDateRangeType(e.target.value)}
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last15">Last 15 Days</option>
                <option value="last30">Last 30 Days</option>
                <option value="last6months">Last 6 Months</option>
                <option value="custom">Custom Range</option>
              </select>

              <select
                value={filterBlock}
                onChange={(e) => setFilterBlock(e.target.value)}
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
              >
                <option value="all">All Status</option>
                <option value="active">Active Only</option>
                <option value="blocked">Blocked Only</option>
              </select>

              <select
                value={filterUPid}
                onChange={(e) => setFilterUPid(e.target.value)}
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
              >
                <option value="all">All Users</option>
                <option value="pid">PID Users</option>
                <option value="organic">Organic Users</option>
              </select>

              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                className="w-full px-4 py-3 border-2 border-main-border rounded-xl text-base font-semibold
                           focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                           transition-all duration-200"
              >
                <option value="desc">Newest First</option>
                <option value="asc">Oldest First</option>
              </select>

              <button
                onClick={handleApplyFilters}
                className="w-full h-full bg-main-blue text-main-white font-bold text-base rounded-xl
                           hover:bg-main-blue/90 transition-all duration-200 shadow-md py-3"
              >
                Apply
              </button>
            </div>

            {dateRangeType === "custom" && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mt-2">
                <input
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="lg:col-span-1 w-full px-4 py-3 border-2 border-main-border rounded-xl text-base
                             focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                             transition-all duration-200"
                />
                <input
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="lg:col-span-1 w-full px-4 py-3 border-2 border-main-border rounded-xl text-base
                             focus:outline-none focus:border-main-blue focus:ring-4 focus:ring-main-blue/10 
                             transition-all duration-200"
                />
              </div>
            )}
          </div>

          {usersSelectedIds.length > 0 && (
            <div className="mt-4 flex items-center justify-between p-4 bg-main-blue/10 rounded-xl border-2 border-main-blue/20">
              <span className="text-main-font font-semibold">
                {usersSelectedIds.length} user(s) selected
              </span>
              <button
                onClick={() => openDeleteConfirmation(usersSelectedIds)}
                className="bg-main-delete text-main-white px-6 py-2 rounded-xl font-semibold 
                           hover:bg-main-delete/90 transition-all duration-200 flex items-center gap-2"
              >
                <FaTrash />
                Delete Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* TABLE */}
      <div className="bg-main-white rounded-3xl shadow-2xl border border-main-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-main-th border-b-2 border-main-border">
                <th className="p-5 text-center w-16">
                  <input
                    type="checkbox"
                    checked={usersSelectedIds.length === usersData.length && usersData.length > 0}
                    onChange={handleSelectAll}
                    className="w-5 h-5 cursor-pointer"
                  />
                </th>
                {["#", "Name", "Email", "Device", "PID", "Purchases", "Last Login", "Status", "Actions"].map((col) => (
                  <th key={col} className="p-5 text-left">
                    <span className="text-main-font font-bold text-sm uppercase tracking-wide">{col}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {usersLoading ? (
                <tr>
                  <td colSpan="10" className="p-16">
                    <div className="flex flex-col items-center justify-center gap-4">
                      <span className="w-12 h-12 border-4 border-main-blue/30 border-t-main-blue rounded-full animate-spin"></span>
                      <p className="text-main-subfont font-medium animate-pulse">Loading...</p>
                    </div>
                  </td>
                </tr>
              ) : usersData.length === 0 ? (
                <tr>
                  <td colSpan="10" className="p-16">
                    <div className="flex flex-col items-center gap-4">
                      <div className="p-6 bg-main-bg rounded-full">
                        <FaUsers className="text-6xl text-main-subfont/30" />
                      </div>
                      <div className="text-center space-y-2">
                        <p className="text-main-font font-semibold text-xl">No users found</p>
                        <p className="text-main-subfont text-base max-w-md">
                          {usersSearch || filterBlock !== "all" ? "Try adjusting your filters" : "No users registered yet"}
                        </p>
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                usersData.map((row, index) => (
                  <tr
                    key={row._id}
                    className="border-b border-main-border/50 hover:bg-main-thHover transition-all duration-200 cursor-pointer"
                    onClick={(e) => {
                      if (!e.target.closest('input[type="checkbox"]') && !e.target.closest("button")) {
                        navigate(`/admin/manage/user/info/${row._id}`);
                      }
                    }}
                  >
                    <td className="p-5 text-center">
                      <input
                        type="checkbox"
                        checked={usersSelectedIds.includes(row._id)}
                        onChange={() => handleSelectOne(row._id)}
                        className="w-5 h-5 cursor-pointer"
                      />
                    </td>
                    <td className="p-5">
                      <span className="text-main-font font-semibold text-base mono">
                        {(usersPagination.page - 1) * usersPagination.limit + index + 1}
                      </span>
                    </td>
                    <td className="p-5">
                      <div className="flex items-center gap-3">
                        {row.u_profile && (
                          <img src={row.u_profile} alt={row.u_name} className="w-10 h-10 rounded-full object-cover" />
                        )}
                        <span className="text-main-font font-semibold text-base">{row.u_name}</span>
                      </div>
                    </td>
                    <td className="p-5">
                      <span className="text-main-font font-medium text-base">{row.u_email}</span>
                    </td>
                    <td className="p-5">
                      <span className="mono text-main-subfont text-sm px-3 py-1.5 bg-main-bg rounded-lg">{row.u_device}</span>
                    </td>
                    <td className="p-5">
                      <span className="mono text-main-font font-semibold text-sm">{row.u_pid || "N/A"}</span>
                    </td>
                    <td className="p-5">
                      <div className="flex justify-center">
                        <span className="px-4 py-2 bg-main-blue/10 text-main-blue font-bold text-base rounded-xl">
                          {row.u_purchased || 0}
                        </span>
                      </div>
                    </td>
                    <td className="p-5">
                      <div className="text-center space-y-1">
                        <div className="text-main-font text-sm font-semibold mono">
                          {formatDateTime(row.u_lastLogin, "DD/MM/YYYY")}
                        </div>
                        <div className="text-main-subfont text-xs mono">
                          {formatDateTime(row.u_lastLogin, "hh:mm:ss A")}
                        </div>
                      </div>
                    </td>
                    <td className="p-5">
                      <div className="flex justify-center">
                        <span
                          className={`flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm ${row.u_status ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                            }`}
                        >
                          {row.u_status ? <><FaCheckCircle /> Active</> : <><FaBan /> Blocked</>}
                        </span>
                      </div>
                    </td>
                    <td className="p-5">
                      <div className="flex justify-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); openDeleteConfirmation([row._id]); }}
                          className="text-main-delete font-semibold hover:bg-main-delete/10 px-5 py-2.5 rounded-xl transition-all duration-200 flex items-center gap-2 border-2 border-transparent hover:border-main-delete/20"
                        >
                          <FaTrash className="text-xs" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        {usersPagination.totalPages > 1 && (
          <div className="p-6 border-t-2 border-main-border flex justify-between items-center">
            <span className="text-main-subfont text-sm font-medium">
              Showing {(usersPagination.page - 1) * usersPagination.limit + 1} to{" "}
              {Math.min(usersPagination.page * usersPagination.limit, usersPagination.total)} of{" "}
              {usersPagination.total} entries
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => setUsersPagination({ ...usersPagination, page: usersPagination.page - 1 })}
                disabled={usersPagination.page === 1}
                className="px-4 py-2 rounded-xl border-2 border-main-border font-semibold text-sm
                           disabled:opacity-40 disabled:cursor-not-allowed hover:bg-main-blue hover:text-main-white
                           hover:border-main-blue transition-all duration-200"
              >
                Previous
              </button>
              <div className="flex gap-1">
                {[...Array(usersPagination.totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setUsersPagination({ ...usersPagination, page: i + 1 })}
                    className={`w-10 h-10 rounded-xl font-bold text-sm transition-all duration-200 ${usersPagination.page === i + 1
                      ? "bg-main-blue text-main-white"
                      : "border-2 border-main-border hover:bg-main-blue/10"
                      }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setUsersPagination({ ...usersPagination, page: usersPagination.page + 1 })}
                disabled={usersPagination.page === usersPagination.totalPages}
                className="px-4 py-2 rounded-xl border-2 border-main-border font-semibold text-sm
                           disabled:opacity-40 disabled:cursor-not-allowed hover:bg-main-blue hover:text-main-white
                           hover:border-main-blue transition-all duration-200"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-main-white w-full max-w-lg rounded-3xl shadow-2xl border-2 border-main-border overflow-hidden">
            <div className="bg-gradient-to-r from-main-delete to-main-delete/90 p-8 text-main-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-main-white/10 rounded-full -mr-32 -mt-32"></div>
              <div className="relative z-10 flex flex-col items-center text-center gap-4">
                <div className="p-5 bg-main-white/20 rounded-2xl backdrop-blur-sm">
                  <FaExclamationTriangle className="text-5xl" />
                </div>
                <div>
                  <h2 className="text-3xl font-bold mb-2">Confirm Deletion</h2>
                  <p className="text-main-white/90 text-sm">This action cannot be undone</p>
                </div>
              </div>
            </div>
            <div className="p-8">
              <div className="bg-main-bg/50 border-2 border-main-border rounded-2xl p-6 mb-6">
                <p className="text-main-font text-center leading-relaxed">
                  You are about to delete{" "}
                  <span className="mono font-bold text-main-blue">{deleteTarget?.length || 0}</span>{" "}
                  {deleteTarget?.length === 1 ? "user" : "users"}.
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
                  className="flex-1 px-6 py-4 text-main-subfont font-bold hover:text-main-font 
                             hover:bg-main-bg rounded-2xl transition-all duration-200 border-2 border-main-border"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="flex-1 bg-main-delete text-main-white px-6 py-4 rounded-2xl font-bold 
                             hover:bg-main-delete/90 shadow-lg hover:shadow-xl transition-all duration-300 
                             flex items-center justify-center gap-2"
                >
                  <FaTrash />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Users;